import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

import accountsAPI from '@/api/accounts';
import Button, { BUTTON_KIND } from '@/components/Button';
import FreePlanExistingOrgDetail from '@/components/SettingsPage/ManagePlan/FreePlanExistingOrgDetail';
import InactivePlanDetail from '@/components/SettingsPage/ManagePlan/InactivePlanDetail';
import PrepaidPlanDetail from '@/components/SettingsPage/ManagePlan/PrepaidPlanDetail';
import { getTierByVariantId } from '@/helpers/sealSubscriptions';
import { capitalize } from '@/helpers/strings';
import useAuth from '@/hooks/useAuth';
import useSubscriptionStatus from '@/hooks/useSubscriptionStatus';
import {
  IPlatformSubscriptionFull,
  IPlatformSubscriptionInvoice,
  SubscriptionTypes,
} from '@/types/IOrganisation';

import { InfoPanelContainer } from '../InfoPanelContainer';
import InfoPanelDivider from '../InfoPanelDivider';
import CancellationPolicyModal from './ManagePlan/CancellationPolicyModal';
import { CancellationConfirmationModal } from './ManagePlan/PlanManagement';
import PurchasingAdditionalCardModal from './ManagePlan/PurchasingAdditionalCardModal';

function getSubscriptionLabel(subscription: IPlatformSubscriptionFull | null) {
  if (subscription === null) {
    return '';
  }

  const isAnnualSubscription = subscription.billing_interval === '1 year';

  if (!isAnnualSubscription) {
    const name = subscription.items[0].title;
    const quantity = subscription.items[0].quantity;

    return `${name} (${quantity} profiles)`;
  }

  const tier = getTierByVariantId(subscription.items[0].variant_id);
  if (tier === undefined) {
    return '1-4 profiles';
  }

  return tier.label;
}

export default function ManagePlan() {
  const { orgID } = useAuth();
  const { isFree, isInactive } = useSubscriptionStatus();

  const hasFreePlanForExistingOrg = isFree;
  const hasInactivePlan = isInactive;

  const { data: subscriptionData } = useQuery({
    queryKey: ['getSubscription', orgID],
    queryFn: getSubscription,
    enabled: orgID !== undefined && !hasFreePlanForExistingOrg,
  });
  const {
    mutateAsync: updatePaymentMethod,
    isPending: isUpdatingPaymentMethod,
  } = useMutation({
    mutationFn: accountsAPI.sendPaymentMethodUpdateEmail,
    onError: error => {
      toast.error('Could not send email, please try again!', {
        richColors: true,
        description: `${error.message}`,
      });
    },
    onSuccess: () => {
      toast.success('Email sent!', {
        richColors: true,
        description:
          'Check your email for the link to update your payment method.',
      });
    },
  });

  const [isCancellationPolicyModalOpen, setIsCancellationPolicyModalOpen] =
    useState(false);
  const [
    isPurchasingAdditionalCardModalOpen,
    setIsPurchasingAdditionalCardModalOpen,
  ] = useState(false);
  const [
    isCancellationConfirmationModalOpen,
    setIsCancellationConfirmationModalOpen,
  ] = useState(false);

  const isAnnualSubscription =
    subscriptionData?.subscription?.billing_interval === '1 year';
  const isScheduledForCancellation =
    subscriptionData?.subscription?.cancelled_at !== null;

  const tier = isAnnualSubscription
    ? getTierByVariantId(
        subscriptionData?.subscription?.items[0].variant_id ?? '',
      )
    : undefined;
  const isFreeTier = isAnnualSubscription && !tier;
  const hasInvoices =
    (subscriptionData?.subscription?.invoices?.length ?? 0) > 0;
  const hasBilling =
    (subscriptionData?.subscription?.billing_attempts?.length ?? 0) > 0;

  async function getSubscription() {
    if (orgID === undefined) {
      return;
    }

    const res = await accountsAPI.getSubscription(orgID);
    return res.data;
  }

  const getPlanPrice = (subscription: IPlatformSubscriptionFull) => {
    if (subscription.items.length === 0) {
      return '-';
    }

    if (subscription.items[0].original_amount === 0) {
      return 'Free';
    }

    return `$${subscription.items[0].original_amount}`;
  };

  if (
    subscriptionData &&
    subscriptionData.subscription?.type === SubscriptionTypes.PREPAID
  ) {
    return <PrepaidPlanDetail />;
  }

  const planData = [
    {
      title: 'Plan type',
      element: subscriptionData ? (
        <span className="text-gray-500 text-sm">
          {getSubscriptionLabel(subscriptionData.subscription)}
        </span>
      ) : (
        <Skeleton className="w-20 h-5 rounded-full" />
      ),
    },
    {
      title: 'Created at',
      element: subscriptionData ? (
        <span className="text-gray-500 text-sm">
          {subscriptionData.subscription
            ? format(
                parseISO(subscriptionData.subscription.order_placed ?? ''),
                'dd/MM/yy HH:mm a',
              )
            : '-'}
        </span>
      ) : (
        <Skeleton className="w-32 h-5 rounded-full" />
      ),
    },
    {
      title: 'Status',
      element: subscriptionData ? (
        subscriptionData.subscription ? (
          <span className="text-emerald-800 font-medium bg-emerald-50 rounded-full text-xs px-2 py-1">
            Active
          </span>
        ) : (
          <span className="text-red-500 font-medium bg-red-50 rounded-full text-xs px-2 py-1">
            Inactive
          </span>
        )
      ) : (
        <Skeleton className="w-12 h-5 rounded-full" />
      ),
    },
    {
      title: 'Price',
      element: subscriptionData ? (
        <span className="text-gray-500 text-sm">
          {subscriptionData.subscription
            ? getPlanPrice(subscriptionData.subscription)
            : '-'}
        </span>
      ) : (
        <Skeleton className="w-10 h-5 rounded-full" />
      ),
    },
  ];

  if (hasBilling) {
    planData.splice(2, 0, {
      title: `Renews every ${isAnnualSubscription ? 'year' : 'month'}`,
      element: subscriptionData ? (
        <span className="text-sm text-gray-500">
          {subscriptionData.subscription?.renewal_payment ? (
            <>
              Next:{' '}
              {format(
                new Date(
                  subscriptionData.subscription.renewal_payment?.date ?? '',
                ),
                'dd/MM/yyyy',
              )}
            </>
          ) : (
            '-'
          )}
        </span>
      ) : (
        <Skeleton className="w-48 h-5 rounded-full" />
      ),
    });
  }

  const billingData = [
    {
      title: 'Customer',
      element: subscriptionData ? (
        subscriptionData.subscription ? (
          <span className="text-sm text-gray-500">
            {subscriptionData.subscription.first_name}&nbsp;
            {subscriptionData.subscription.last_name}
          </span>
        ) : (
          <span className="text-sm text-gray-500">-</span>
        )
      ) : null,
    },
    {
      title: 'Purchasing email',
      element: subscriptionData ? (
        subscriptionData.subscription ? (
          <span className="text-sm text-gray-500">
            {subscriptionData.subscription.email}
          </span>
        ) : (
          <></>
        )
      ) : null,
    },
    {
      title: 'Billing address',
      element: subscriptionData ? (
        subscriptionData.subscription ? (
          <span className="text-sm text-gray-500 text-right">
            {subscriptionData.subscription.billing.first_name}{' '}
            {subscriptionData.subscription.billing.last_name}
            <br />
            {subscriptionData.subscription.billing.address1}{' '}
            {subscriptionData.subscription.billing.address2}
            <br />
            {subscriptionData.subscription.billing.city},{' '}
            {subscriptionData.subscription.billing.province}{' '}
            {subscriptionData.subscription.billing.zip}
            <br />
            {subscriptionData.subscription.billing.country}
          </span>
        ) : (
          <></>
        )
      ) : (
        <Skeleton className="w-24 h-16 rounded-md" />
      ),
    },
  ];

  if (hasBilling) {
    billingData.push({
      title: 'Payment method',
      element: subscriptionData ? (
        subscriptionData.subscription ? (
          <div>
            <div className="flex flex-col items-end text-sm text-gray-500">
              <span>
                {capitalize(subscriptionData.subscription.payment_method.brand)}{' '}
                ending in {subscriptionData.subscription.payment_method.last4}
              </span>
              <span>
                Expires on{' '}
                {subscriptionData.subscription.payment_method.exp_month}/
                {subscriptionData.subscription.payment_method.exp_year}
              </span>
            </div>
          </div>
        ) : (
          <></>
        )
      ) : (
        <Skeleton className="" />
      ),
    });
  }

  const paymentStatusLabel = (status: string) =>
    status === 'completed' ? 'Paid' : status === '' ? 'Scheduled' : status;

  if (subscriptionData?.subscription === null && hasInactivePlan) {
    return (
      <InactivePlanDetail totalProfiles={subscriptionData.total_profiles} />
    );
  }

  if (hasFreePlanForExistingOrg) {
    return <FreePlanExistingOrgDetail />;
  }

  return (
    <div>
      <InfoPanelContainer
        title="Plan"
        description="View your plan details, including profile tier, status, renewal date, and pricing."
        additionalContent={
          <button
            type="button"
            className="text-brand-500 font-medium text-sm underline"
            onClick={() => setIsPurchasingAdditionalCardModalOpen(true)}
          >
            Purchasing additional cards
          </button>
        }
      >
        <div className="space-y-4">
          {planData.map((detail, index) => (
            <div key={index} className="flex justify-between">
              <span className="font-medium text-sm">{detail.title}:</span>
              {detail.element}
            </div>
          ))}
        </div>
      </InfoPanelContainer>

      <InfoPanelDivider />

      {!isFreeTier && (
        <>
          <InfoPanelContainer
            title={hasBilling ? 'Billing' : 'Invoices'}
            description={
              hasBilling
                ? 'View past and upcoming billing dates with payment status.'
                : 'View past and upcoming invoices with payment status.'
            }
          >
            <div className="divide-y divide-gray-200">
              {subscriptionData ? (
                subscriptionData.subscription ? (
                  <>
                    {hasBilling &&
                      subscriptionData.subscription.billing_attempts.map(
                        (attempt, index) => (
                          <div
                            key={index}
                            className={clsx('flex justify-between py-4', {
                              'border-t border-gray-200': index !== 0,
                            })}
                          >
                            <span className="font-medium text-sm">
                              {format(parseISO(attempt.date), 'dd/MM/yyyy')}
                            </span>
                            <span className="text-gray-500 text-sm">
                              {paymentStatusLabel(attempt.status)}
                            </span>
                          </div>
                        ),
                      )}
                    {hasInvoices &&
                      subscriptionData.subscription.invoices.map(
                        (invoice, index) => (
                          <InvoiceItem key={index} invoice={invoice} />
                        ),
                      )}
                  </>
                ) : (
                  <>no subscription found</>
                )
              ) : (
                <div className="divide-y divide-gray-200">
                  <span className="flex justify-between py-4">
                    <Skeleton className="w-20 h-5 rounded-full" />
                    <Skeleton className="w-12 h-5 rounded-full" />
                  </span>
                  <span className="flex justify-between py-4">
                    <Skeleton className="w-20 h-5 rounded-full" />
                    <Skeleton className="w-12 h-5 rounded-full" />
                  </span>
                </div>
              )}
            </div>
          </InfoPanelContainer>

          <InfoPanelDivider />
        </>
      )}

      <InfoPanelContainer
        title="Customer & payment details"
        description="Manage your saved credit card and billing address details linked to your account"
        footerContent={
          hasBilling && (
            <Button
              buttonText="Edit payment method"
              kind={BUTTON_KIND.WHITE}
              onClick={orgID ? () => updatePaymentMethod(orgID) : undefined}
              loading={isUpdatingPaymentMethod}
            />
          )
        }
      >
        <div className="space-y-4">
          {billingData.map((detail, index) => (
            <div key={index} className="flex justify-between">
              <span className="font-medium text-sm">{detail.title}</span>
              {detail.element}
            </div>
          ))}
        </div>
      </InfoPanelContainer>

      {!isFreeTier && (
        <>
          <InfoPanelDivider />
          <InfoPanelContainer
            title="Cancel plan"
            additionalContent={
              <button
                type="button"
                className="flex text-brand-500 font-medium underline text-sm mt-2"
                onClick={() => setIsCancellationPolicyModalOpen(true)}
              >
                View cancellation policy
              </button>
            }
          >
            {isScheduledForCancellation ? (
              <div className="flex justify-between py-4">
                <span className="text-gray-500 text-sm">
                  Scheduled to be cancelled at
                </span>
                {subscriptionData ? (
                  subscriptionData.subscription &&
                  subscriptionData.subscription.cancelled_at ? (
                    <span className="text-gray-500 text-sm">
                      {format(
                        parseISO(subscriptionData.subscription.cancelled_at),
                        'dd/MM/yy',
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-500 text-sm">-</span>
                  )
                ) : (
                  <Skeleton className="w-16 h-5 rounded-full" />
                )}
              </div>
            ) : (
              <Button
                buttonText="Cancel subscription"
                className="place-self-end"
                kind={BUTTON_KIND.LIGHT_RED}
                onClick={() => setIsCancellationConfirmationModalOpen(true)}
              />
            )}

            <CancellationPolicyModal
              isOpen={isCancellationPolicyModalOpen}
              setIsOpen={setIsCancellationPolicyModalOpen}
            />
            <CancellationConfirmationModal
              isOpen={isCancellationConfirmationModalOpen}
              setIsOpen={setIsCancellationConfirmationModalOpen}
            />
          </InfoPanelContainer>
        </>
      )}

      <PurchasingAdditionalCardModal
        isOpen={isPurchasingAdditionalCardModalOpen}
        setIsOpen={setIsPurchasingAdditionalCardModalOpen}
      />
    </div>
  );
}

function InvoiceItem({ invoice }: { invoice: IPlatformSubscriptionInvoice }) {
  return (
    <div className="flex justify-between py-4">
      <span className="font-medium text-sm">
        {format(parseISO(invoice.date), 'dd/MM/yyyy')}
      </span>

      {invoice.payment_status === '' && !invoice.invoice_url && (
        <span className="text-gray-500 text-sm">Scheduled</span>
      )}

      {invoice.payment_status === '' && invoice.invoice_url && (
        <span className="space-x-2">
          {invoice.invoice_download_url && (
            <span className="text-gray-500">
              <a
                href={invoice.invoice_download_url}
                target="_blank"
                className="pr-2"
              >
                Download invoice
              </a>
              |
            </span>
          )}
          <a href={invoice.invoice_url} target="_blank">
            Pay now
          </a>
        </span>
      )}

      {invoice.payment_status === 'completed' && (
        <span className="text-gray-500 text-sm">Paid</span>
      )}
    </div>
  );
}
